import React from 'react';
import {AuthenticatedPages} from './pages/AuthenticatedPages/AuthenticatedPages';
import {LoadingPage} from './pages/LoadingPage/LoadingPage';
import {GuestPages} from './pages/GuestPages/GuestPages';

import {DndProvider} from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import {LicenseInfo} from '@mui/x-license-pro';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import {useSession} from './hooks/useSession';
import {ROLES} from './constants/roles';
import {PAGES} from './constants/pages';
import {hasRole} from './utils/hasRole';

import './App.css';
import './output.css';
import {auth} from './services/auth';
import {Alert, Button} from '@mui/material';

LicenseInfo.setLicenseKey(
  'b681ffbf8a8a6fefa8f28397d6fa8a94Tz03ODkzNCxFPTE3MzE4NDEzOTAwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='
);

const App = () => {
  const session = useSession();

  const theme = createTheme({
    palette: {
      primary: {
        main: '#2c65de',
      },
      secondary: {
        main: '#256861',
      },
    },
    typography: {
      h1: {
        fontSize: '2rem',
        '@media (max-width:600px)': {
          fontSize: '1.5rem',
        },
      },
      h2: {
        fontSize: '1.75rem',
        '@media (max-width:600px)': {
          fontSize: '1.3rem',
        },
      },
      h3: {
        fontSize: '1.5rem',
        '@media (max-width:600px)': {
          fontSize: '1.15rem',
        },
      },
      h4: {
        fontSize: '1.25rem',
        '@media (max-width:600px)': {
          fontSize: '1.0rem',
        },
      },

      h5: {
        fontSize: '1.15rem',
        '@media (max-width:600px)': {
          fontSize: '0.95rem',
        },
      },
      h6: {
        fontSize: '1rem',
        '@media (max-width:600px)': {
          fontSize: '0.85rem',
        },
      },

      body1: {
        fontSize: '1rem',
        '@media (max-width:600px)': {
          fontSize: '0.875rem',
        },
      },
      body2: {
        fontSize: '11px',
        lineHeight: '13px',
        '@media (max-width:600px)': {
          fontSize: '10px',
        },
      },
      caption: {
        fontSize: '0.7rem',
        '@media (max-width:600px)': {
          fontSize: '0.6rem',
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <DndProvider backend={HTML5Backend}>
        {auth.getOverrideToken() !== null && session && (
          <Alert
            sx={{
              fontSize: '13px',
              '@media (max-width:600px)': {
                fontSize: '11px',
              },
            }}
            severity="warning"
            variant="filled"
            action={
              <Button
                color="inherit"
                variant="outlined"
                size="small"
                onClick={() => auth.logOut()}
                sx={{
                  fontSize: '13px',
                  '@media (max-width:600px)': {
                    fontSize: '9px',
                  },
                }}
              >
                Return to your own account
              </Button>
            }
          >
            You are currently in view-only mode as{' '}
            <strong>{session.user.name}</strong>.
          </Alert>
        )}

        {session === undefined && <LoadingPage/>}

        {session === null && <GuestPages/>}

        {session?.user?.roles && (
          <>
            {hasRole(session.user.roles, ROLES.GUIDE) && (
              <PAGES.GUIDE session={session}/>
            )}

            {hasRole(session.user.roles, ROLES.SALES_PARTNER) && (
              <PAGES.SALES_PARTNER session={session}/>
            )}

            { (hasRole(session.user.roles, ROLES.BOOKING_MANAGER) && session.user?.accommodation_providers && session.user.accommodation_providers.length) ? (
              <PAGES.ACCOMMODATION_BOOKING_MANAGER
                session={session}
                data={{
                  serviceReservationId: new URLSearchParams(
                    window.location.search
                  ).get('view_id'),
                }}
              />
            ) : <></>}

            {(hasRole(session.user.roles, ROLES.BOOKING_MANAGER) || hasRole(session.user.roles, ROLES.EVENT_STAFF)) && (
              <PAGES.BOOKING_MANAGER
                session={session}
                data={{
                  serviceReservationId: new URLSearchParams(
                    window.location.search
                  ).get('view_id'),
                }}
              />
            )}

            {!hasRole(session.user.roles, ROLES.GUIDE) &&
              !hasRole(session.user.roles, ROLES.EVENT_STAFF) &&
              !hasRole(session.user.roles, ROLES.BOOKING_MANAGER) && (
                <AuthenticatedPages session={session}/>
              )}
          </>
        )}
      </DndProvider>
    </ThemeProvider>
  );
};

export default App